import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, X, Building2, Clock, CheckCircle, AlertCircle, PauseCircle, Archive, Gift, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL_FORMS = [
  { value: 'einzelunternehmen', label: 'Einzelunternehmen' },
  { value: 'gbr', label: 'GbR (Gesellschaft bürgerlichen Rechts)' },
  { value: 'ug', label: 'UG (haftungsbeschränkt)' },
  { value: 'gmbh', label: 'GmbH' },
  { value: 'ag', label: 'AG' },
  { value: 'kg', label: 'KG (Kommanditgesellschaft)' },
  { value: 'ohg', label: 'OHG (Offene Handelsgesellschaft)' },
];
const INDUSTRIES = [
  { value: 'tankstelle', label: 'Tankstelle' },
  { value: 'einzelhandel', label: 'Einzelhandel' },
  { value: 'gastronomie', label: 'Gastronomie' },
  { value: 'andere', label: 'Andere' },
];
const BRANDS = ['JET', 'Shell', 'Aral', 'Esso', 'TotalEnergies', 'OMV', 'AVIA',
  'Agip/Eni', 'HEM', 'Star/ORLEN', 'Raiffeisen', 'Westfalen', 'bft', 'Freie Tankstelle', 'Sonstige'];

const EMPTY_COMP = {
  first_name: '', last_name: '', company_name: '', legal_form: '', industry: 'tankstelle',
  brand: '', email: '', password: '', street: '', house_number: '', postal_code: '', city: '',
  location_name: '', station_number: '', replace_existing: false,
};

export const CustomerManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [archivedCustomers, setArchivedCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchive, setShowArchive] = useState(false);
  const [showCompDialog, setShowCompDialog] = useState(false);
  const [compForm, setCompForm] = useState(EMPTY_COMP);
  const [compSaving, setCompSaving] = useState(false);
  const [compResult, setCompResult] = useState(null);

  const setC = (k, v) => setCompForm(f => ({ ...f, [k]: v }));

  const submitComplimentary = async (replaceExisting = false) => {
    if (!compForm.first_name || !compForm.last_name || !compForm.company_name ||
        !compForm.legal_form || !compForm.email || !compForm.password) {
      toast.error('Bitte alle Pflichtfelder ausfüllen (Vorname, Nachname, Firma, Rechtsform, E-Mail, Passwort)');
      return;
    }
    if (compForm.industry === 'tankstelle' && !compForm.brand) {
      toast.error('Bitte Gesellschafter / Marke auswählen');
      return;
    }
    setCompSaving(true);
    try {
      const res = await axios.post(`${API}/superadmin/customers/create-complimentary`, {
        ...compForm, replace_existing: replaceExisting,
      });
      setCompResult(res.data);
      toast.success('Complimentary-Account angelegt');
      fetchCustomers();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'email_exists') {
        if (d.is_protected) {
          toast.error(`E-Mail bereits vergeben – Account ist ${d.existing_status || 'aktiv'} und geschützt. Nicht automatisch ersetzbar.`);
        } else if (window.confirm(
          `E-Mail bereits vergeben (Status: ${d.existing_status || 'unbekannt'}). ` +
          `Das ist vermutlich ein hängender/unbezahlter Account (z.B. abgebrochener Checkout). ` +
          `Soll dieser alte Account gelöscht und sauber neu als Complimentary angelegt werden?`
        )) {
          setCompSaving(false);
          return submitComplimentary(true);
        }
      } else {
        toast.error(typeof d === 'string' ? d : 'Fehler beim Anlegen des Accounts');
      }
    } finally {
      setCompSaving(false);
    }
  };

  const closeCompDialog = () => {
    setShowCompDialog(false);
    setCompForm(EMPTY_COMP);
    setCompResult(null);
  };

  useEffect(() => {
    fetchCustomers();
    fetchArchivedCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/customers`);
      setCustomers(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Kunden');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchivedCustomers = async () => {
    try {
      const response = await axios.get(`${API}/superadmin/customers/archive`);
      setArchivedCustomers(response.data);
    } catch (error) {
      console.error('Fehler beim Laden der archivierten Kunden', error);
    }
  };

  const approveCustomer = async (customerId) => {
    try {
      await axios.patch(`${API}/superadmin/customers/${customerId}/approve`);
      toast.success('Kunde freigeschaltet');
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Freischalten');
    }
  };

  const blockCustomer = async (customerId) => {
    try {
      await axios.patch(`${API}/superadmin/customers/${customerId}/block`);
      toast.success('Kunde gesperrt');
      fetchCustomers();
    } catch (error) {
      toast.error('Fehler beim Sperren');
    }
  };

  const getStatusBadge = (customer) => {
    // Determine customer status based on stripe_status and is_manually_blocked
    const stripe_status = customer.stripe_status;
    const stripe_subscription_id = customer.stripe_subscription_id;
    const is_manually_blocked = customer.is_manually_blocked;
    const status = customer.status;
    
    // Gekündigt
    if (status === 'canceled') {
      return (
        <Badge className="bg-gray-500 text-white flex items-center gap-1">
          <Archive className="h-3 w-3" />
          Gekündigt
        </Badge>
      );
    }
    
    // Wartend (Pending)
    if (!stripe_subscription_id || stripe_status === 'incomplete') {
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="h-3 w-3" />
          Wartend
        </Badge>
      );
    }
    
    // Aktiv
    if (stripe_status === 'active' || stripe_status === 'trialing') {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <CheckCircle className="h-3 w-3" />
          Aktiv
        </Badge>
      );
    }
    
    // Zahlungsproblem
    if (stripe_status === 'past_due' || stripe_status === 'unpaid') {
      return (
        <Badge className="bg-orange-500 text-white flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          Zahlungsproblem
        </Badge>
      );
    }
    
    // Inaktiv
    if (stripe_status === 'paused' || is_manually_blocked) {
      return (
        <Badge className="bg-gray-600 text-white flex items-center gap-1">
          <PauseCircle className="h-3 w-3" />
          Inaktiv
        </Badge>
      );
    }
    
    // Default fallback
    return (
      <Badge variant="secondary" className="flex items-center gap-1">
        <Clock className="h-3 w-3" />
        Unbekannt
      </Badge>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope, sans-serif'}} data-testid="customers-title">
              Kundenverwaltung
            </h1>
            <p className="text-muted-foreground mt-2">
              {showArchive ? 'DSGVO-konformes Archiv gekündigter Kunden' : 'Verwalten Sie alle aktiven Kunden'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="default"
              onClick={() => setShowCompDialog(true)}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700"
              data-testid="open-complimentary-dialog-btn"
            >
              <Gift className="h-4 w-4" />
              Complimentary-Account anlegen
            </Button>
            <Button
              variant={showArchive ? "outline" : "default"}
              onClick={() => setShowArchive(!showArchive)}
              className="flex items-center gap-2"
            >
              {showArchive ? (
                <>
                  <Building2 className="h-4 w-4" />
                  Aktive Kunden
                </>
              ) : (
                <>
                  <Archive className="h-4 w-4" />
                  Archiv ({archivedCustomers.length})
                </>
              )}
            </Button>
          </div>
        </div>

        <ComplimentaryDialog
          open={showCompDialog}
          onClose={closeCompDialog}
          form={compForm}
          setC={setC}
          onSubmit={() => submitComplimentary(false)}
          saving={compSaving}
          result={compResult}
        />

        {!showArchive ? (
          // Active Customers View
          <div className="grid gap-4">
            {customers.map((customer) => (
              <Card key={customer.id} data-testid={`customer-card-${customer.id}`}>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-md">
                        <Building2 className="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-xl">{customer.company_name}</CardTitle>
                          {getStatusBadge(customer)}
                        </div>
                        <CardDescription>
                          <div className="mt-1 space-y-1">
                            <p>Kundennummer: <span className="font-mono">{customer.customer_number}</span></p>
                            <p>Kontakt: {customer.contact_name} ({customer.contact_email})</p>
                            <p>Branche: {customer.industry}</p>
                            <p>Registriert: {new Date(customer.created_at).toLocaleDateString('de-DE')}</p>
                          </div>
                        </CardDescription>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    {customer.status === 'pending' && (
                      <Button
                        onClick={() => approveCustomer(customer.id)}
                        size="sm"
                        data-testid={`approve-button-${customer.id}`}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        Freischalten
                      </Button>
                    )}
                    {customer.status === 'active' && (
                      <Button
                        onClick={() => blockCustomer(customer.id)}
                        variant="destructive"
                        size="sm"
                        data-testid={`block-button-${customer.id}`}
                      >
                        <X className="mr-2 h-4 w-4" />
                        Sperren
                      </Button>
                    )}
                    {customer.status === 'blocked' && (
                      <Button
                        onClick={() => approveCustomer(customer.id)}
                        variant="outline"
                        size="sm"
                        data-testid={`unblock-button-${customer.id}`}
                      >
                        <Check className="mr-2 h-4 w-4" />
                        Entsperren
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}

            {customers.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50" />
                  <p className="mt-4 text-muted-foreground">Noch keine Kunden registriert</p>
                </CardContent>
              </Card>
            )}
          </div>
        ) : (
          // Archive View - DSGVO-compliant
          <div className="space-y-4">
            <Card className="border-gray-300 bg-gray-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-gray-700">
                  <Archive className="h-5 w-5" />
                  Archivierte Kunden (DSGVO-konform)
                </CardTitle>
                <CardDescription>
                  Diese Ansicht zeigt nur Kunden-IDs und Kündigungsdaten. Keine personenbezogenen Daten werden angezeigt.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {archivedCustomers.length > 0 ? (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-4 p-3 bg-gray-200 rounded font-semibold text-sm text-gray-700">
                      <div>Kundennummer</div>
                      <div>Erstellt am</div>
                      <div>Gekündigt am</div>
                    </div>
                    {archivedCustomers.map((customer) => (
                      <div key={customer.customer_id} className="grid grid-cols-3 gap-4 p-3 bg-white rounded border border-gray-200 text-sm">
                        <div className="font-mono text-gray-700">{customer.customer_number || customer.customer_id}</div>
                        <div className="text-gray-600">
                          {customer.created_at ? new Date(customer.created_at).toLocaleDateString('de-DE') : '-'}
                        </div>
                        <div className="text-gray-600">
                          {customer.canceled_at ? new Date(customer.canceled_at).toLocaleDateString('de-DE') : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-8 text-center text-gray-500">
                    <Archive className="mx-auto h-12 w-12 text-gray-400" />
                    <p className="mt-4">Keine archivierten Kunden vorhanden</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};


function ComplimentaryDialog({ open, onClose, form, setC, onSubmit, saving, result }) {
  const field = "space-y-1";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="complimentary-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Gift className="h-5 w-5 text-emerald-600" />
            Complimentary-Account anlegen
          </DialogTitle>
          <DialogDescription>
            Vollwertiger Account wie bei der normalen Registrierung – aber kostenlos, ohne Stripe,
            höchstes Paket (Professional AI), alle Module, zeitlich unbegrenzt.
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-3 py-2" data-testid="complimentary-result">
            <div className="flex items-center gap-2 text-emerald-700 font-semibold">
              <CheckCircle className="h-5 w-5" /> Account erfolgreich angelegt
            </div>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm space-y-1">
              <div><span className="text-muted-foreground">Kundennummer:</span> <strong>{result.customer_number}</strong></div>
              <div><span className="text-muted-foreground">Admin-Login:</span> <strong>{result.admin_email}</strong></div>
              <div><span className="text-muted-foreground">Freigeschaltete Module:</span> <strong>{result.modules_enabled}</strong></div>
              {result.replaced_existing && <div className="text-amber-600">Hinweis: Ein hängender Vorgänger-Account mit dieser E-Mail wurde ersetzt.</div>}
            </div>
            <p className="text-xs text-muted-foreground">
              Der Mitarbeiter-Login wird anschließend im Account unter <strong>Personal → Mitarbeiter hinzufügen</strong> angelegt.
            </p>
            <DialogFooter>
              <Button onClick={onClose} data-testid="complimentary-done-btn">Fertig</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4 py-1">
            {/* Schritt 1 */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schritt 1 · Firma & Login</p>
              <div className="grid grid-cols-2 gap-3">
                <div className={field}>
                  <Label>Vorname *</Label>
                  <Input value={form.first_name} onChange={e => setC('first_name', e.target.value)} data-testid="comp-first-name" />
                </div>
                <div className={field}>
                  <Label>Nachname *</Label>
                  <Input value={form.last_name} onChange={e => setC('last_name', e.target.value)} data-testid="comp-last-name" />
                </div>
                <div className={`${field} col-span-2`}>
                  <Label>Firmenname *</Label>
                  <Input value={form.company_name} onChange={e => setC('company_name', e.target.value)} placeholder="z.B. Mustermann Tankstellen GmbH" data-testid="comp-company" />
                </div>
                <div className={field}>
                  <Label>Rechtsform *</Label>
                  <Select value={form.legal_form} onValueChange={v => setC('legal_form', v)}>
                    <SelectTrigger data-testid="comp-legal-form"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                    <SelectContent>
                      {LEGAL_FORMS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className={field}>
                  <Label>Branche *</Label>
                  <Select value={form.industry} onValueChange={v => setC('industry', v)}>
                    <SelectTrigger data-testid="comp-industry"><SelectValue placeholder="Wählen…" /></SelectTrigger>
                    <SelectContent>
                      {INDUSTRIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.industry === 'tankstelle' && (
                <div className="mt-3 space-y-1">
                  <Label>Gesellschafter / Marke *</Label>
                  <div className="flex flex-wrap gap-1.5">
                    {BRANDS.map(b => (
                      <button
                        key={b} type="button" onClick={() => setC('brand', b === 'Sonstige' ? '' : b)}
                        data-testid={`comp-brand-${b}`}
                        className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition-all ${
                          form.brand === b ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-background border-border hover:border-emerald-400'
                        }`}
                      >{b}</button>
                    ))}
                  </div>
                  <Input value={form.brand} onChange={e => setC('brand', e.target.value)} placeholder="Marke (frei eingebbar bei 'Sonstige')" className="mt-1.5" data-testid="comp-brand-input" />
                  <p className="text-[11px] text-muted-foreground">Beeinflusst die automatischen Produktvorschläge.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className={field}>
                  <Label>E-Mail *</Label>
                  <Input type="email" value={form.email} onChange={e => setC('email', e.target.value)} data-testid="comp-email" />
                </div>
                <div className={field}>
                  <Label>Passwort *</Label>
                  <Input value={form.password} onChange={e => setC('password', e.target.value)} data-testid="comp-password" />
                </div>
              </div>
            </div>

            {/* Schritt 2 */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">Schritt 2 · Standort</p>
              <div className="grid grid-cols-2 gap-3">
                <div className={field}>
                  <Label>Straße</Label>
                  <Input value={form.street} onChange={e => setC('street', e.target.value)} data-testid="comp-street" />
                </div>
                <div className={field}>
                  <Label>Nr.</Label>
                  <Input value={form.house_number} onChange={e => setC('house_number', e.target.value)} data-testid="comp-house" />
                </div>
                <div className={field}>
                  <Label>PLZ</Label>
                  <Input value={form.postal_code} onChange={e => setC('postal_code', e.target.value)} data-testid="comp-plz" />
                </div>
                <div className={field}>
                  <Label>Stadt</Label>
                  <Input value={form.city} onChange={e => setC('city', e.target.value)} data-testid="comp-city" />
                </div>
                <div className={field}>
                  <Label>Standortname</Label>
                  <Input value={form.location_name} onChange={e => setC('location_name', e.target.value)} placeholder="z.B. JET Hamburg City" data-testid="comp-locname" />
                </div>
                <div className={field}>
                  <Label>Stationsnummer (optional)</Label>
                  <Input value={form.station_number} onChange={e => setC('station_number', e.target.value)} data-testid="comp-station" />
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose} disabled={saving}>Abbrechen</Button>
              <Button onClick={onSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="comp-submit-btn">
                {saving ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Wird angelegt…</> : 'Account anlegen'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
